if (!customElements.get('natunora-offer-reviews')) {
  customElements.define('natunora-offer-reviews', class extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const track = this.querySelector('.natunora-review__track');
      const cards = [...this.querySelectorAll('.natunora-review__card')];
      if (!track || !cards.length) return;
      const mobile = matchMedia('(max-width: 989px)');
      const desktop = Math.min(cards.length - 1, Math.max(0, Number(this.dataset.desktopReview) || 0));
      const status = this.querySelector('[data-review-status]');
      const loop = cards.length > 1;
      const clone = card => {
        const copy = card.cloneNode(true);
        copy.dataset.reviewClone = '';
        copy.classList.remove('is-desktop');
        copy.setAttribute('aria-hidden', 'true');
        copy.inert = true;
        [copy, ...copy.querySelectorAll('*')].forEach(node => {
          node.removeAttribute('id');
          node.removeAttribute('data-shopify-editor-block');
        });
        return copy;
      };
      if (loop) {
        track.prepend(clone(cards.at(-1)));
        track.append(clone(cards[0]));
      }
      const slides = [...track.children];
      const first = loop ? 1 : 0;
      let slot = first;
      let current = 0;
      let moving = false;
      let touching = false;
      let gesture = null;
      let width = track.clientWidth;
      const position = index => slides[index].offsetLeft - slides[0].offsetLeft;
      const logical = index => (index - first + cards.length) % cards.length;
      const fit = () => {
        const active = mobile.matches ? current : desktop;
        track.style.height = `${cards[active].getBoundingClientRect().height}px`;
        cards.forEach((card, i) => card.setAttribute('aria-hidden', i !== active));
      };
      const readPosition = () => {
        const step = loop ? position(1) : 0;
        if (!step) return;
        slot = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / step)));
        current = logical(slot);
        fit();
      };
      const settle = () => {
        clearTimeout(this.settleTimer);
        if (!mobile.matches || touching) return;
        readPosition();
        if (loop && (slot === 0 || slot === slides.length - 1)) {
          slot = current + first;
          track.scrollTo({ left: position(slot), behavior: 'instant' });
        }
        moving = false;
        if (status) status.textContent = `Kundenstimme ${current + 1} von ${cards.length}`;
      };
      const go = next => {
        if (!mobile.matches || moving || !loop) return;
        slot = Math.max(0, Math.min(slides.length - 1, next));
        current = logical(slot);
        moving = true;
        track.scrollTo({ left: position(slot), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        fit();
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(settle, 200);
      };
      this.querySelectorAll('[data-review-step]').forEach(button => button.addEventListener('click', () => go(slot + Number(button.dataset.reviewStep)), options));
      track.addEventListener('keydown', event => {
        if (!mobile.matches || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        go(slot + (event.key === 'ArrowRight' ? 1 : -1));
      }, options);
      track.addEventListener('pointerdown', event => {
        if (!mobile.matches || !loop || !event.isPrimary || event.button !== 0) return;
        settle();
        touching = true;
        moving = false;
        clearTimeout(this.settleTimer);
        track.scrollTo({ left: position(slot), behavior: 'instant' });
        gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, slot, left: position(slot), dx: 0, horizontal: false };
        track.setPointerCapture(event.pointerId);
      }, options);
      track.addEventListener('pointermove', event => {
        if (!gesture || event.pointerId !== gesture.id) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        if (!gesture.horizontal) {
          if (Math.abs(dx) < 8 || Math.abs(dx) <= Math.abs(dy)) return;
          gesture.horizontal = true;
          track.style.scrollSnapType = 'none';
        }
        event.preventDefault();
        const step = position(1);
        gesture.dx = Math.max(-step, Math.min(step, dx));
        track.scrollTo({ left: gesture.left - gesture.dx, behavior: 'instant' });
      }, { ...options, passive: false });
      const release = event => {
        if (!gesture || event.pointerId !== gesture.id) return;
        const ended = gesture;
        gesture = null;
        touching = false;
        track.style.removeProperty('scroll-snap-type');
        if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
        if (ended.horizontal) {
          const threshold = Math.min(40, track.clientWidth * 0.12);
          const direction = Math.abs(ended.dx) >= threshold ? (ended.dx < 0 ? 1 : -1) : 0;
          moving = false;
          go(ended.slot + direction);
        } else settle();
      };
      track.addEventListener('pointerup', release, options);
      track.addEventListener('pointercancel', release, options);
      track.addEventListener('lostpointercapture', release, options);
      track.addEventListener('dragstart', event => event.preventDefault(), options);
      track.addEventListener('scroll', () => {
        if (!mobile.matches || !loop) return;
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(readPosition);
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(settle, 200);
      }, { ...options, passive: true });
      track.addEventListener('scrollend', settle, options);
      const resetPosition = () => {
        clearTimeout(this.settleTimer);
        cancelAnimationFrame(this.scrollFrame);
        moving = false;
        touching = false;
        gesture = null;
        track.style.removeProperty('scroll-snap-type');
        slot = current + first;
        track.scrollTo({ left: mobile.matches ? position(slot) : 0, behavior: 'instant' });
        track.tabIndex = mobile.matches && loop ? 0 : -1;
        fit();
      };
      this.resizeObserver = new ResizeObserver(() => {
        if (width !== track.clientWidth) {
          width = track.clientWidth;
          resetPosition();
        } else fit();
      });
      cards.forEach(card => this.resizeObserver.observe(card));
      mobile.addEventListener('change', () => {
        current = 0;
        resetPosition();
      }, options);
      this.addEventListener('shopify:block:select', event => {
        const index = cards.indexOf(event.target.closest('.natunora-review__card'));
        if (index >= 0) go(index + first);
      }, options);
      resetPosition();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      clearTimeout(this.settleTimer);
      this.querySelectorAll('[data-review-clone]').forEach(card => card.remove());
    }
  });
}
